import React, { useState, useMemo } from 'react';
import { Building, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, ChevronUp, Search, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../apiConfig/api';
import * as XLSX from 'xlsx';

const LoadingText = ({ width = "w-40", height = "h-6" }) => (
  <div className={`inline-block ${width} ${height} animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded relative overflow-hidden`}>
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
  </div>
);

const LoadingItem = () => (
  <div className="grid grid-cols-4 gap-4 py-3">
    <div className="col-span-1 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-3 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="flex justify-center"><div className="h-4 bg-gray-200 rounded w-12"></div></div>
    <div className="flex justify-center"><div className="h-4 bg-gray-200 rounded w-12"></div></div>
    <div className="flex justify-center"><div className="h-4 bg-gray-200 rounded w-12"></div></div>
  </div>
);

const LoadingRows = () => (
  <div className="p-4 animate-pulse space-y-3">
    {[1, 2, 3].map(i => (
      <div key={i} className="grid grid-cols-5 gap-4">
        <div className="h-4 bg-gray-200 rounded col-span-2"></div>
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded"></div>
      </div>
    ))}
  </div>
);

// Empty state with shimmer effect
const EmptyStateShimmer = ({ searchTerm }) => (
  <div className="py-8 px-4 text-center">
    <div className="inline-block animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded h-16 w-16 mx-auto mb-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
    </div>
    <div className="flex flex-col items-center gap-2">
      <LoadingText width="w-64" height="h-4" />
      <LoadingText width="w-48" height="h-3" />
    </div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
    <AlertCircle className="h-5 w-5" /><span>{message}</span>
  </div>
);

const SearchBar = ({ searchTerm, onSearchChange, onClearSearch, sortConfig, onSort }) => (
  <div className="mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
    <div className="relative flex-1 max-w-md">
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search wards, zones..."
        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
      <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      {searchTerm && (
        <button onClick={onClearSearch} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
    <div className="flex gap-2 overflow-x-auto w-full sm:w-auto">
      <button
        onClick={() => onSort('completed')}
        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50"
      >
        Completed {sortConfig.field === 'completed' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
      </button>
      <button
        onClick={() => onSort('pending')}
        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50"
      >
        Pending {sortConfig.field === 'pending' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
      </button>
      <button
        onClick={() => onSort('total')}
        className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50"
      >
        Total {sortConfig.field === 'total' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
      </button>
    </div>
  </div>
);

// Zone Details Modal Component - Updated with Excel download
const ZoneDetailsModal = ({ isOpen, onClose, zoneData, onCompletedClick, onPendingClick, onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  if (!isOpen || !zoneData) return null;
  
  // Filter reports based on search query
  const filteredReports = zoneData.reports.filter(report => {
    if (searchQuery.trim() === '') return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (report.ward_name && report.ward_name.toLowerCase().includes(query)) ||
      (report.org_name && report.org_name.toLowerCase().includes(query))
    );
  });

const downloadZoneExcel = () => {
  // Check if we have data
  if (!zoneData || !zoneData.reports || zoneData.reports.length === 0) {
    console.error('No zone data available for export');
    return;
  }

  const currentDate = new Date().toLocaleDateString('en-GB');
  const title = `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}`;

  const wsData = [
    [title], // Title row
    [], // Empty row
    ['Ward Name', 'Organization', 'Completed', 'Pending', 'Total']
  ];
  
  // Add data rows
  zoneData.reports.forEach(report => {
    wsData.push([
      report.ward_name || 'N/A',
      report.org_name || 'N/A',
      parseInt(report.completed) || 0,
      parseInt(report.pending) || 0,
      parseInt(report.total) || 0
    ]);
  });
  
  // Add summary row
  const totalCompleted = zoneData.reports.reduce((sum, report) => sum + (parseInt(report.completed) || 0), 0);
  const totalPending = zoneData.reports.reduce((sum, report) => sum + (parseInt(report.pending) || 0), 0);
  const totalSum = zoneData.reports.reduce((sum, report) => sum + (parseInt(report.total) || 0), 0);
  
  wsData.push(['TOTAL', '', totalCompleted, totalPending, totalSum]);

  // Verify we have data before creating workbook
  if (wsData.length <= 3) {
    console.error('No data to export');
    return;
  }

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws['!cols'] = [
    { width: 25 },
    { width: 30 },
    { width: 12 },
    { width: 12 },
    { width: 12 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Zone Details');
  XLSX.writeFile(wb, `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}.xlsx`);
};
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button 
                  onClick={onBack}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600" />
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-800">
                {zoneData.zoneName} - Ward Details
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadZoneExcel}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                title="Download Zone Details"
              >
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Excel
              </button>
              <button 
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
          <div className="mt-3 flex gap-6 text-sm text-gray-600">
            <span>Completed: <span className="font-medium text-green-600">{zoneData.stats.completed}</span></span>
            <span>Pending: <span className="font-medium text-orange-600">{zoneData.stats.pending}</span></span>
            <span>Total: <span className="font-medium text-gray-700">{zoneData.stats.total}</span></span>
          </div>
        </div>
        
        {/* Search input */}
        <div className="px-4 py-3 border-b border-gray-200 sticky top-20 bg-white">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search wards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="overflow-auto p-4">
          {filteredReports.length > 0 ? (
            <div className="grid gap-4">
              {filteredReports.map((report, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Ward Name</p>
                      <p className="font-medium">{report.ward_name}</p>
                      <p className="text-xs text-gray-500 mt-1">{report.org_name}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Completed</p>
                        {report.completed > 0 ? (
                          <button onClick={() => onCompletedClick(report)} className="font-medium text-green-600 hover:text-green-700 transition-colors hover:underline">
                            {report.completed}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-400">0</span>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Pending</p>
                        {report.pending > 0 ? (
                          <button onClick={() => onPendingClick(report)} className="font-medium text-orange-600 hover:text-orange-700 transition-colors hover:underline">
                            {report.pending}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-400">0</span>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Total</p>
                        <p className="font-medium text-gray-700">{report.total}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {searchQuery.trim() !== '' 
                ? "No matching wards found" 
                : "No ward details available"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Asset Details Modal Component
const AssetDetailsModal = ({ isOpen, onClose, assetDetails, isLoading, wardInfo, onAssetClick, status, onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  if (!isOpen) return null;
  
  // Filter assets based on search query
  const filteredAssets = searchQuery.trim() === '' 
    ? assetDetails 
    : assetDetails.filter(asset => {
        const query = searchQuery.toLowerCase();
        return (
          (asset.street_name && asset.street_name.toLowerCase().includes(query)) ||
          (asset.owner && asset.owner.toLowerCase().includes(query)) ||
          (asset.eb_no && asset.eb_no.toLowerCase().includes(query))
        );
      });

  // Excel download function for asset details
const downloadAssetExcel = () => {
  const currentDate = new Date().toLocaleDateString('en-GB');
  const title = `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}`;

  const wsData = [
    [title], // Title row
    [], // Empty row
    ['Street Name', 'Owner Name', 'EB Number', 'Status']
  ];
  
  // Add data rows
  filteredAssets.forEach(asset => {
    wsData.push([
      asset.street_name || 'N/A',
      asset.owner || 'N/A',
      asset.eb_no || 'N/A',
      status === 'completed' ? 'Completed' : 'Pending'
    ]);
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws['!cols'] = [
    { width: 30 },
    { width: 25 },
    { width: 15 },
    { width: 12 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Asset Details');
  XLSX.writeFile(wb, `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}.xlsx`);
};
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button 
                  onClick={onBack}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600" />
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-800">
                Asset Details - {wardInfo?.ward_name || 'N/A'} - {status === 'completed' ? 'Survey Completed' : 'Survey Pending'}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={downloadAssetExcel}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                title="Download Asset Details"
              >
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Excel
              </button>
              <button 
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Add search input below the header */}
        <div className="px-4 py-3 border-b border-gray-200 sticky top-16 bg-white">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search by street name, owner or EB number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        <div className="overflow-auto p-4">
          {isLoading ? (
            <LoadingRows />
          ) : filteredAssets.length > 0 ? (
            <div className="grid gap-4">
              {filteredAssets.map((asset, index) => (
                <div 
                  key={index} 
                  onClick={() => status === 'completed' ? onAssetClick(asset.id) : null}
                  className={`border border-gray-200 rounded-lg p-4 transition-all ${
                    status === 'completed' 
                      ? 'hover:shadow-lg hover:border-gray-300 cursor-pointer' 
                      : 'cursor-default'
                  }`}
                >
                  <div className="grid grid-cols-2 gap-4">
  <div>
    <p className="text-sm text-gray-500">Street Name</p>
    <p className="font-medium">{asset.street_name || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Owner Name</p>
    <p className="font-medium">{asset.owner || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">EB Number</p>
    <p className="font-medium">{asset.eb_no || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Status</p>
    <p className={`font-medium ${
      (status === 'completed' || asset.status === 'Completed') ? 'text-green-600' : 'text-orange-600'
    }`}>
      {status === 'completed' ? 'Completed' : 'Pending'}
    </p>
  </div>
  {status === 'completed' && asset.created_at && (
    <>
      <div>
        <p className="text-sm text-gray-500">Created Date</p>
        <p className="font-medium text-sm">
          {new Date(asset.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric'
          }) + ' ' + 
          new Date(asset.created_at).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}
        </p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Usage</p>
        <p className="font-medium">{asset.usage || 'N/A'}</p>
      </div>
    </>
  )}
</div>
                  {status === 'completed' && (
                    <div className="mt-3 flex justify-end">
                      <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                        View Details <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {searchQuery.trim() !== '' 
                ? "No matching assets found" 
                : `No ${status} assets available`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const UnauthorizedSurveyHeader = () => (
  <div className="hidden lg:grid grid-cols-4 gap-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg px-4">
    <div className="col-span-1">
      <h4 className="text-sm font-medium text-gray-600">Ward Details</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Completed</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Pending</h4>
    </div>
    <div className="text-center">
      <h4 className="text-sm font-medium text-gray-600">Total</h4>
    </div>
  </div>
);

const ZoneGroupHeader = ({ zoneName, onToggle, stats, onCompletedClick, onPendingClick }) => (
  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded" onClick={onToggle}>
        <ChevronRight className="h-4 w-4" />
        <h3 className="font-medium text-gray-800">{zoneName}</h3>
      </div>
      <div className="flex gap-6 text-sm">
        <span>
          Completed: {stats.completed > 0 ? (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onCompletedClick(zoneName);
              }} 
              className="font-medium text-green-600 hover:text-green-700 transition-colors hover:underline"
            >
              {stats.completed}
            </button>
          ) : (
            <span className="font-medium text-gray-400">0</span>
          )}
        </span>
        <span>
          Pending: {stats.pending > 0 ? (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onPendingClick(zoneName);
              }} 
              className="font-medium text-orange-600 hover:text-orange-700 transition-colors hover:underline"
            >
              {stats.pending}
            </button>
          ) : (
            <span className="font-medium text-gray-400">0</span>
          )}
        </span>
        <span>Total: <span className="font-medium text-gray-700">{stats.total}</span></span>
      </div>
    </div>
  </div>
);

const UnauthorizedSurveyItem = ({ report, onCompletedClick, onPendingClick, onAssetClick }) => (
  <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 py-3 border-b border-gray-100 last:border-0 px-4 hover:bg-gray-50">
    <div className="col-span-1">
      <h3 className="font-medium text-gray-800">{report.ward_name}</h3>
      <div className="text-xs text-gray-500 mt-1">
        <p>{report.org_name}</p>
        <p>{report.zone_name}</p>
      </div>
    </div>
    <div className="flex justify-between lg:justify-center items-center">
      <span className="text-sm text-gray-500 lg:hidden">Completed:</span>
      {report.completed > 0 ? (
        <button onClick={() => onCompletedClick(report)} className="font-medium text-green-600 hover:text-green-700 transition-colors hover:underline">
          {report.completed}
        </button>
      ) : (
        <span className="font-medium text-gray-400">0</span>
      )}
    </div>
    <div className="flex justify-between lg:justify-center items-center">
      <span className="text-sm text-gray-500 lg:hidden">Pending:</span>
      {report.pending > 0 ? (
        <button onClick={() => onPendingClick(report)} className="font-medium text-orange-600 hover:text-orange-700 transition-colors hover:underline">
          {report.pending}
        </button>
      ) : (
        <span className="font-medium text-gray-400">0</span>
      )}
    </div>
    <div className="flex justify-between lg:justify-center items-center">
      <span className="text-sm text-gray-500 lg:hidden">Total:</span>
      <span className="font-medium text-gray-700">{report.total || '0'}</span>
    </div>
  </div>
);

const PaginationControls = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, showingFrom, showingTo }) => (
  <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-4 py-3 border-t border-gray-200 bg-white">
    <div className="text-sm text-gray-700">
      Showing <span className="font-medium">{showingFrom}</span>{' '}
      to <span className="font-medium">{showingTo}</span>{' '}
      of <span className="font-medium">{totalItems}</span> results
    </div>
    <div className="flex items-center gap-2">
      <button onClick={() => onPageChange(1)} disabled={currentPage === 1} 
        className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
        <ChevronsLeft className="h-4 w-4" />
      </button>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} 
        className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="px-4 py-1 text-sm text-gray-700">
        Page {currentPage} of {totalPages}
      </span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} 
        className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
        <ChevronRight className="h-4 w-4" />
      </button>
      <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} 
        className="p-1 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
        <ChevronsRight className="h-4 w-4" />
      </button>
    </div>
  </div>
);

// Stats summary component
const StatsSummary = ({ assetReports }) => {
  // Calculate total stats
  const totalStats = assetReports.reduce((acc, report) => {
    acc.total += parseInt(report.total) || 0;
    acc.completed += parseInt(report.completed) || 0;
    acc.pending += parseInt(report.pending) || 0;
    return acc;
  }, { total: 0, completed: 0, pending: 0 });

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
        <h3 className="text-sm font-medium text-blue-700 mb-1">Total Assets</h3>
        <p className="text-2xl font-bold text-blue-800">{totalStats.total.toLocaleString()}</p>
      </div>
      <div className="bg-green-50 rounded-lg p-4 border border-green-100">
        <h3 className="text-sm font-medium text-green-700 mb-1">Completed</h3>
        <p className="text-2xl font-bold text-green-800">{totalStats.completed.toLocaleString()}</p>
      </div>
      <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
        <h3 className="text-sm font-medium text-orange-700 mb-1">Pending</h3>
        <p className="text-2xl font-bold text-orange-800">{totalStats.pending.toLocaleString()}</p>
      </div>
    </div>
  );
};

export const UnauthorizedSurveyTable = ({ isLoading, assetReports, onCompletedClick, onNavigateToReport }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [expandedZones, setExpandedZones] = useState({});
  const [groupByZone, setGroupByZone] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: null, direction: null });
  
  // Modal states
  const [modalStack, setModalStack] = useState([]);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [selectedWardInfo, setSelectedWardInfo] = useState(null);
  const [selectedZoneData, setSelectedZoneData] = useState(null);
  const [assetDetails, setAssetDetails] = useState([]);
  const [isLoadingAssetDetails, setIsLoadingAssetDetails] = useState(false);
  const [assetModalStatus, setAssetModalStatus] = useState('completed');

  const sortData = (data) => {
    if (!sortConfig.field) return data;
    
    const sortedData = [...data].sort((a, b) => {
      // Convert string numbers to actual numbers for comparison
      const aValue = parseInt(a[sortConfig.field]) || 0;
      const bValue = parseInt(b[sortConfig.field]) || 0;
      
      if (sortConfig.direction === 'desc') {
        return bValue - aValue;
      }
      return aValue - bValue;
    });
  
    return sortedData;
  };
  
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      // Start with desc (high to low) on first click
      direction: prev.field === field ? 
        (prev.direction === 'desc' ? 'asc' : 'desc') : 
        'desc'
    }));
  };

const downloadExcel = () => {
  // Calculate totals
  let totalSum = 0;
  let completedSum = 0;
  let pendingSum = 0;
  
  filteredData.forEach((report) => {
    totalSum += parseInt(report.total) || 0;
    completedSum += parseInt(report.completed) || 0;
    pendingSum += parseInt(report.pending) || 0;
  });

  // Create title and date
  const currentDate = new Date().toLocaleDateString('en-GB');
  const title = `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}`;

  // Create worksheet data based on current view
  let wsData;
  
  if (groupByZone && groupedData) {
    // Zone grouped view
    wsData = [
      [title], // Title row
      [], // Empty row
      ['Zone Name', 'Ward Name', 'Organization', 'Total Count', 'Completed Count', 'Pending Count']
    ];
    
    // Add data rows grouped by zone
    Object.entries(groupedData).forEach(([zoneName, zoneData]) => {
      zoneData.reports.forEach(report => {
        wsData.push([
          zoneName,
          report.ward_name || 'N/A',
          report.org_name || 'N/A',
          parseInt(report.total) || 0,
          parseInt(report.completed) || 0,
          parseInt(report.pending) || 0
        ]);
      });
    });
  } else {
    // Ward list view
    wsData = [
      [title], // Title row
      [], // Empty row
      ['Ward Name', 'Zone Name', 'Organization', 'Total Count', 'Completed Count', 'Pending Count']
    ];
    
    // Add data rows
    filteredData.forEach((report) => {
      wsData.push([
        report.ward_name || 'N/A',
        report.zone_name || 'N/A',
        report.org_name || 'N/A',
        parseInt(report.total) || 0,
        parseInt(report.completed) || 0,
        parseInt(report.pending) || 0
      ]);
    });
  }
  
  // Add summary row
  wsData.push(['TOTAL', '', '', totalSum, completedSum, pendingSum]);

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws['!cols'] = [
    { width: 25 }, // Zone/Ward Name
    { width: 25 }, // Ward/Zone Name  
    { width: 30 }, // Organization
    { width: 15 }, // Total Count
    { width: 18 }, // Completed Count
    { width: 15 }  // Pending Count
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Asset Survey Details');
  XLSX.writeFile(wb, `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}.xlsx`);
};
  // Zone click handler - opens zone detail popup
  const handleZoneClick = (zoneName) => {
    const zoneData = groupedData[zoneName];
    setSelectedZoneData({ zoneName, ...zoneData });
    setIsZoneModalOpen(true);
    setModalStack(['zone']);
  };

  // New handlers for zone-level clicks
  const handleZoneCompletedClick = async (zoneName) => {
    const zoneData = groupedData[zoneName];
    
    // Filter to only show wards with completed > 0
    const completedWards = zoneData.reports.filter(report => report.completed > 0);
    
    // If only one ward has completed items, go directly to that ward's assets
    if (completedWards.length === 1) {
      handleCompletedClick(completedWards[0]);
    } else {
      // Show zone modal with only completed wards
      setSelectedZoneData({ 
        zoneName, 
        reports: completedWards, 
        stats: zoneData.stats 
      });
      setIsZoneModalOpen(true);
      setModalStack(['zone']);
    }
  };

  const handleZonePendingClick = async (zoneName) => {
    const zoneData = groupedData[zoneName];
    
    // Filter to only show wards with pending > 0
    const pendingWards = zoneData.reports.filter(report => report.pending > 0);
    
    // If only one ward has pending items, go directly to that ward's assets
    if (pendingWards.length === 1) {
      handlePendingClick(pendingWards[0]);
    } else {
      // Show zone modal with only pending wards
      setSelectedZoneData({ 
        zoneName, 
        reports: pendingWards, 
        stats: zoneData.stats 
      });
      setIsZoneModalOpen(true);
      setModalStack(['zone']);
    }
  };

  const handleCompletedClick = async (report, fromZone = false) => {
    setSelectedWardInfo(report);
    setIsAssetModalOpen(true);
    setIsLoadingAssetDetails(true);
    setAssetModalStatus('completed');
    
    // Update modal stack
    if (fromZone) {
      setModalStack(['zone', 'asset']);
    } else {
      setModalStack(['asset']);
    }

    try {
      // Get org_code from localStorage
      const orgCode = localStorage.getItem('organization_code');
      
      const response = await api.get('/asset/report', {
        params: {
          org: orgCode,
          ward: report.ward_name,
          status: 'completed'
        }
      });
      setAssetDetails(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetDetails([]);
    } finally {
      setIsLoadingAssetDetails(false);
    }
  };

  const handlePendingClick = async (report, fromZone = false) => {
    setSelectedWardInfo(report);
    setIsAssetModalOpen(true);
    setIsLoadingAssetDetails(true);
    setAssetModalStatus('pending');
    
    // Update modal stack
    if (fromZone) {
      setModalStack(['zone', 'asset']);
    } else {
      setModalStack(['asset']);
    }

    try {
      // Get org_code from localStorage
      const orgCode = localStorage.getItem('organization_code');
      
      const response = await api.get('/asset/report', {
        params: {
          org: orgCode,
          zone: report.zone_name,
          ward: report.ward_name,
          status: 'active'
        }
      });
      setAssetDetails(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetDetails([]);
    } finally {
      setIsLoadingAssetDetails(false);
    }
  };

  const handleAssetClick = (assetId) => {
    if (!assetId) return;
    onNavigateToReport(assetId);
  };

  const handleBackNavigation = () => {
    const newStack = [...modalStack];
    newStack.pop();
    setModalStack(newStack);
    
    if (newStack.length === 0) {
      // Close all modals
      setIsAssetModalOpen(false);
      setIsZoneModalOpen(false);
    } else if (newStack[newStack.length - 1] === 'zone') {
      // Go back to zone modal
      setIsAssetModalOpen(false);
    }
  };

  const handleCloseAllModals = () => {
    setModalStack([]);
    setIsAssetModalOpen(false);
    setIsZoneModalOpen(false);
    setSelectedWardInfo(null);
    setSelectedZoneData(null);
    setAssetDetails([]);
  };

  const filteredData = useMemo(() => {
    return sortData(assetReports.filter(report => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        report.ward_name?.toLowerCase().includes(searchLower) ||
        report.zone_name?.toLowerCase().includes(searchLower) ||
        report.org_name?.toLowerCase().includes(searchLower)
      );
    }));
  }, [assetReports, searchTerm, sortConfig]);

  const groupedData = useMemo(() => {
    if (!groupByZone) return null;
    
    // First group the data
    const grouped = filteredData.reduce((acc, report) => {
      const zoneName = report.zone_name || 'Unassigned';
      if (!acc[zoneName]) {
        acc[zoneName] = {
          reports: [],
          stats: { completed: 0, pending: 0, total: 0 }
        };
      }
      acc[zoneName].reports.push(report);
      acc[zoneName].stats.completed += parseInt(report.completed) || 0;
      acc[zoneName].stats.pending += parseInt(report.pending) || 0;
      acc[zoneName].stats.total += parseInt(report.total) || 0;
      return acc;
    }, {});
  
    // Sort the zones based on their stats
    const sortedEntries = Object.entries(grouped).sort(([, a], [, b]) => {
      if (sortConfig.field) {
        const aValue = parseInt(a.stats[sortConfig.field]) || 0;
        const bValue = parseInt(b.stats[sortConfig.field]) || 0;
        return sortConfig.direction === 'desc' ? bValue - aValue : aValue - bValue;
      }
      return 0;
    });
  
    // Convert back to object after sorting
    return Object.fromEntries(sortedEntries);
  }, [filteredData, groupByZone, sortConfig]);

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const showingFrom = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, totalItems);
  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };
  const toggleZone = (zoneName) => {
    setExpandedZones(prev => ({...prev, [zoneName]: !prev[zoneName]}));
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortConfig]);

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Asset detail</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setGroupByZone(!groupByZone)}
              className={`px-4 py-2 rounded-md border transition-colors ${
                !groupByZone 
                  ? 'border-blue-600 text-blue-600 bg-blue-50' 
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {!groupByZone ? 'Group by Zone' : 'Show Ward data'}
            </button>
            <button
              onClick={downloadExcel}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              title="Download Excel"
            >
              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
          </div>
        </div>

        {!isLoading && <StatsSummary assetReports={assetReports} />}

        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onClearSearch={handleClearSearch}
          sortConfig={sortConfig}
          onSort={handleSort}
        />

        <div className="rounded-lg border border-gray-200">
          {!groupByZone && <UnauthorizedSurveyHeader sortConfig={sortConfig} onSort={handleSort} />}
          
          <div className="divide-y divide-gray-200">
            {isLoading ? (
              <div className="px-4">{[1, 2, 3].map((i) => (<LoadingItem key={i} />))}</div>
            ) : groupByZone ? (
              Object.entries(groupedData || {}).length > 0 ? (
                Object.entries(groupedData || {}).map(([zoneName, data]) => (
                  <div key={zoneName}>
                    <ZoneGroupHeader
                      zoneName={zoneName}
                      onToggle={() => handleZoneClick(zoneName)}
                      stats={data.stats}
                      onCompletedClick={handleZoneCompletedClick}
                      onPendingClick={handleZonePendingClick}
                    />
                  </div>
                ))
              ) : (
                <EmptyStateShimmer searchTerm={searchTerm} />
              )
            ) : (
              getCurrentItems().length > 0 ? (
                getCurrentItems().map((report, index) => (
                  <UnauthorizedSurveyItem
                    key={index}
                    report={report}
                    onCompletedClick={() => handleCompletedClick(report)}
                    onPendingClick={() => handlePendingClick(report)}
                    onAssetClick={handleAssetClick}
                  />
                ))
              ) : (
                <EmptyStateShimmer searchTerm={searchTerm} />
              )
            )}
          </div>

          {!isLoading && !groupByZone && filteredData.length > 0 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              showingFrom={showingFrom}
              showingTo={showingTo}
            />
          )}
        </div>
      </div>

      {/* Zone Details Modal */}
      <ZoneDetailsModal 
        isOpen={isZoneModalOpen}
        onClose={handleCloseAllModals}
        zoneData={selectedZoneData}
        onCompletedClick={(report) => handleCompletedClick(report, true)}
        onPendingClick={(report) => handlePendingClick(report, true)}
        onBack={modalStack.length === 1 ? null : handleBackNavigation}
      />

      {/* Asset Details Modal */}
      <AssetDetailsModal 
        isOpen={isAssetModalOpen}
        onClose={handleCloseAllModals}
        assetDetails={assetDetails}
        isLoading={isLoadingAssetDetails}
        wardInfo={selectedWardInfo}
        onAssetClick={handleAssetClick}
        status={assetModalStatus}
        onBack={modalStack.length === 1 ? null : handleBackNavigation}
      />
    </div>
  );
};